import 'keen-slider/keen-slider.min.css'

/**
 * ProgressiveImageCarousel
 *
 * This component diverged from SwipeGallery to implement a more efficient image loading strategy:
 * - Loads only the first image initially so that we do not affect the page load performance
 * - Preloads the second image on hover to reduce perceived loading time
 * - Loads subsequent images only when the active image is loaded
 *
 * - Added as part of fbdb929e-image-carousel-desktop-cards
 */
import {useCallback, useContext, useMemo, useRef, useState} from 'react'
import {useDispatch, useSelector} from 'react-redux'
import styled from '@emotion/styled'
import {HotelCardContext} from 'components/HotelCardContext'
import {useKeenSlider} from 'keen-slider/react'
import {
  getParametrizedHotelContext,
  getParametrizedHotelInfoContext
} from 'middleware/analytics/selectors'
import {getIsRtlLanguage} from 'modules/meta/selectors'

import {Button} from '@vio/atlas/Button'
import {CurrentPaneIndicators} from '@vio/atlas/components/designSystem/helpers/SwipePanes/CurrentPaneIndicators'
import {Box} from '@vio/atlas/helpers/Box'
import {Icon} from '@vio/atlas/Icon'
import {ImageSources} from '@vio/atlas/Image'
import mirror from '@vio/atlas/src/utils/mirror'
import {cssTheme} from '@vio/atlas/themes'
import {trackEvent} from '@vio/core/src/analytics/modules/actions'
import {
  Action,
  AnalyticsContext,
  Category,
  Entity
} from '@vio/core/src/analytics/types/Events'
import {ImageProvider} from '@vio/core/src/staticContent/types/ImageProvider'
import {debounce} from '@vio/core/src/utils/debounce'

import {SlideImageWithSkeleton} from './SlideImageWithSkeleton'
import {getCardImageSources} from './utils/getCardImageSources'

const ArrowButton = styled(Button)`
  ${mirror()}
  position: absolute;
  top: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  opacity: 0;
  transition: opacity 0.3s ease;

  &.prev {
    left: ${cssTheme.layout.spacing.s400};
    svg {
      transform: translate(-1px, 0);
    }
  }

  &.next {
    right: ${cssTheme.layout.spacing.s400};

    svg {
      transform: translate(1px, 0);
    }
  }

  .--gallery-container:hover & {
    opacity: 1;
  }

  .--gallery-container:hover &.hidden {
    opacity: 0;
  }
`

interface Props {
  images: string[]
  imageProvider: ImageProvider
  isPriorityCard?: boolean
}

const CONTAINER_WIDTH = 278

export const ProgressiveImageCarousel: React.FC<Props> = ({
  images,
  imageProvider,
  isPriorityCard
}) => {
  const [currentSlide, setCurrentSlide] = useState(0)
  const [loaded, setLoaded] = useState(false)
  const [loadedImages, setLoadedImages] = useState<number[]>([0]) // Initially load just the first image

  const dispatch = useDispatch()
  const {hotelId} = useContext(HotelCardContext)
  const isRtl = useSelector(getIsRtlLanguage)

  const analyticsContext = useMemo(
    () => ({
      [AnalyticsContext.HotelContext]: getParametrizedHotelContext(hotelId),
      [AnalyticsContext.HotelInfoContext]:
        getParametrizedHotelInfoContext(hotelId)
    }),
    [hotelId]
  )

  // Track which images we're currently loading to avoid duplicate loading attempts
  const loadingImagesRef = useRef<Set<number>>(new Set())

  const imageSources = useMemo(
    () => images.map(url => getCardImageSources(url, imageProvider)),
    [images, imageProvider]
  )

  // Warms the cache with the same candidate the slide's img will pick
  const preloadImage = useCallback(({src, srcSet}: ImageSources) => {
    const img = new Image()
    if (srcSet) img.srcset = srcSet
    img.src = src
  }, [])

  // Function to load a specific image
  const loadImage = useCallback(
    (index: number) => {
      // Skip if already loaded or currently loading
      if (loadedImages.includes(index) || loadingImagesRef.current.has(index)) {
        return
      }

      // Mark as loading
      loadingImagesRef.current.add(index)

      const sources = imageSources[index]
      if (sources?.src) {
        preloadImage(sources)
      }

      // Update state to indicate this image is loaded
      setLoadedImages(prev => [...prev, index])
    },
    [loadedImages, imageSources, preloadImage]
  )

  // Initialize the slider with consistent slide sizing
  const [sliderRef, instanceRef] = useKeenSlider({
    initial: 0,
    slides: {
      perView: 1,
      spacing: 0
    },
    defaultAnimation: {
      duration: 800
    },
    renderMode: 'performance',
    drag: false,
    rtl: isRtl,
    slideChanged(slider) {
      const newSlide = slider.track.details.rel
      setCurrentSlide(newSlide)

      // Load current and next slide
      if (!loadedImages.includes(newSlide)) {
        loadImage(newSlide)
      }

      const nextSlide = newSlide + 1
      if (nextSlide < images.length && !loadedImages.includes(nextSlide)) {
        loadImage(nextSlide)
      }
    },
    created() {
      setLoaded(true)
    }
  })

  // Handle hover to load the second image
  const handleMouseEnter = useCallback(() => {
    if (images.length > 1 && !loadedImages.includes(1)) {
      loadImage(1)
    }
  }, [images.length, loadedImages, loadImage])

  const handleOnWheel = useMemo(
    () =>
      debounce((e: React.WheelEvent<HTMLDivElement>) => {
        if (Math.abs(e.deltaX) <= Math.abs(e.deltaY)) return
        dispatch(
          trackEvent({
            category: Category.User,
            entity: Entity.HotelCardImageCarousel,
            action: Action.Scrolled,
            analyticsContext
          })
        )
      }, 50),
    [dispatch, analyticsContext]
  )

  return (
    <Box
      position="relative"
      width="100%"
      height="100%"
      onMouseEnter={handleMouseEnter}
      onWheelCapture={handleOnWheel}
      className="--gallery-container"
    >
      <div ref={sliderRef} className="keen-slider" style={{height: '100%'}}>
        {images.map((_, idx) => (
          <div
            key={idx}
            className="keen-slider__slide"
            style={{minWidth: '100%'}}
          >
            <Box
              height="100%"
              width="100%"
              display="flex"
              alignItems="center"
              justifyContent="center"
              overflow="hidden"
              position="relative"
            >
              {loadedImages.includes(idx) && (
                <SlideImageWithSkeleton
                  sources={imageSources[idx]}
                  loading={idx === 0 ? 'eager' : 'lazy'}
                  isPriorityImage={isPriorityCard && idx === 0}
                />
              )}
            </Box>
          </div>
        ))}
      </div>
      {/* Navigation arrows - visible on container hover */}
      {loaded && images.length > 1 && instanceRef.current && (
        <>
          <ArrowButton
            dataId="paginationLeftButton"
            iconStart={<Icon name="ChevronLeft" size="sm" />}
            size="sm"
            variant="tertiary"
            isFloating
            isRounded
            onClick={e => {
              e.stopPropagation()
              e.preventDefault()
              instanceRef.current?.prev()
            }}
            className={`prev ${currentSlide === 0 ? 'hidden' : ''}`}
          />

          <ArrowButton
            dataId="paginationRightButton"
            iconStart={<Icon name="ChevronRight" size="sm" />}
            size="sm"
            variant="tertiary"
            isFloating
            isRounded
            onClick={e => {
              e.preventDefault()
              e.stopPropagation()
              instanceRef.current?.next()
            }}
            className={`next ${
              currentSlide ===
              instanceRef.current.track.details.slides.length - 1
                ? 'hidden'
                : ''
            }`}
          />

          <CurrentPaneIndicators
            amount={images.length}
            currentIndex={currentSlide}
            width={CONTAINER_WIDTH}
            hasDarkGradient={false}
            isRtl={isRtl}
          />
        </>
      )}
    </Box>
  )
}
